import config from '../config';

// Helper functions (kept from your original code)
const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
};

// 1. Load the Backend's RSA Public Key (instead of the static AES secret)
const getRsaPublicKey = async (): Promise<CryptoKey> => {
  const pemHeaderRemoved = config.VITE_RSA_PUBLIC_KEY
    .replace(/-----BEGIN PUBLIC KEY-----/, '')
    .replace(/-----END PUBLIC KEY-----/, '')
    .replace(/\s+/g, '');

  const keyBuffer = base64ToArrayBuffer(pemHeaderRemoved);

  return await crypto.subtle.importKey(
    'spki',
    keyBuffer,
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt']
  );
};

// 2. Updated field-level encryption to use the dynamic AES key and IV passed into it
const encryptField = async (text: string | undefined | null, aesKey: CryptoKey, iv: Uint8Array): Promise<string> => {
  if (!text) return text || '';

  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    const encrypted = await crypto.subtle.encrypt(
      {
        name: 'AES-GCM',
        iv: iv as BufferSource, // Uses the unique request IV
        tagLength: 128
      },
      aesKey, // Uses the unique request AES key
      data
    );

    return arrayBufferToBase64(encrypted);
  } catch (error) {
    console.error('AES field encryption failed:', error);
    return text;
  }
};

interface SecureHeaderMetadata {
  secureKeyHeader: string; // To be placed in 'X-Secure-Key'
  secureIvHeader: string;  // To be placed in 'X-Secure-IV'
  encryptedData: any;      // The payload with encrypted sensitive fields
}

/**
 * Main entry function to prepare your data and headers for an API request
 */
export const encryptPayload = async <T extends Record<string, unknown>>(data: T): Promise<SecureHeaderMetadata> => {
  // If no data, return empty structures safely
  if (!data || typeof data !== 'object') {
    return {
      secureKeyHeader: '',
      secureIvHeader: '',
      encryptedData: data
    };
  }

  // Deep clone the original object to prevent mutating original application state
  const encryptedObject: Record<string, unknown> = JSON.parse(JSON.stringify(data));

  try {
    // A. Generate a completely random, single-use AES key for this request execution
    const transientAesKey = await crypto.subtle.generateKey(
      {
        name: 'AES-GCM',
        length: 256
      },
      true,
      ['encrypt']
    );

    // B. Generate a completely random 12-byte initialization vector (IV)
    const requestIv = crypto.getRandomValues(new Uint8Array(12));

    // C. Find and encrypt the targeted individual sensitive keys inside the payload object
    const sensitiveFields = ['email', 'phone', 'password', 'mobile', 'passphrase', 'new_password', 'first_name', 'last_name', 'customerName', 'customerNameZh'];

    for (const field of sensitiveFields) {
      if (encryptedObject[field] !== undefined && encryptedObject[field] !== null) {
        encryptedObject[field] = await encryptField(String(encryptedObject[field]), transientAesKey, requestIv);
      }
    }

    // D. Export the raw text configuration of our unique AES key
    const rawAesKeyBytes = await crypto.subtle.exportKey('raw', transientAesKey);

    // E. Encrypt that random AES key using the backend server's RSA Public Key
    const rsaPublicKey = await getRsaPublicKey();
    const encryptedKeyBuffer = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      rsaPublicKey,
      rawAesKeyBytes
    );

    // F. Return the transaction bundle
    return {
      secureKeyHeader: arrayBufferToBase64(encryptedKeyBuffer),
      secureIvHeader: arrayBufferToBase64(requestIv.buffer as ArrayBuffer),
      encryptedData: encryptedObject as T
    };

  } catch (error) {
    console.error('Hybrid payload preparation failed, returning original unencrypted payload data', error);
    return {
      secureKeyHeader: '',
      secureIvHeader: '',
      encryptedData: data
    };
  }
};